import React, { useState, useEffect } from 'react';
import Modal from '../../../components/Modal';
import AddPartyForm, { PartyFormData } from './AddPartyForm';
import { 
  getPartyListAPI, 
  savePartyAPI, 
  deletePartyAPI,
  Party,
  PartySearchRequest 
} from '../../../services/adminApi';
import { ToastContainer } from '../../../components/Toast';
import { useToast } from '../../../hooks/useToast';
import { useModulePermission } from '../../../hooks/useModulePermission';
import styles from './PartyMaster.module.css';

const PartyMaster: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [parties, setParties] = useState<Party[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingParty, setEditingParty] = useState<Party | null>(null);
  const { toasts, removeToast, showSuccess, showError } = useToast();
  const permissions = useModulePermission('Party Master');

  // Load parties on component mount
  useEffect(() => {
    loadParties();
  }, []);

  const loadParties = async () => {
    try {
      setLoading(true);
      const searchRequest: PartySearchRequest = {
        searchText: searchQuery,
        pageNo: 1,
        pageSize: 100,
      };
      const response = await getPartyListAPI(searchRequest);
      if (response.isSuccess && response.data) {
        setParties(response.data);
      } else {
        showError(response.message || 'Failed to load parties');
      }
    } catch (error) {
      console.error('Failed to load parties:', error);
      showError('Failed to load parties');
    } finally {
      setLoading(false);
    }
  };

  const handleAddParty = () => {
    setEditingParty(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingParty(null);
  };

  const handleSubmitParty = async (partyData: PartyFormData) => {
    try {
      const partyPayload: Party = {
        id: editingParty?.id,
        partyName: partyData.name,
        mobileNo: partyData.mobile,
        emailId: partyData.email,
        address: partyData.address,
        panNo: partyData.pan,
        isActive: partyData.status === 'active',
        notes: partyData.note,
      };

      const response = await savePartyAPI(partyPayload);
      if (response.isSuccess) {
        showSuccess(editingParty ? 'Party updated successfully!' : 'Party created successfully!');
        await loadParties();
        setIsModalOpen(false);
        setEditingParty(null);
      } else {
        showError(response.message || 'Failed to save party');
      }
    } catch (error) {
      console.error('Failed to save party:', error);
      showError('Failed to save party. Please try again.');
    }
  };

  const handleView = (party: Party) => {
    alert(`Party Details:\n\nName: ${party.partyName}\nPhone: ${party.mobileNo}\nEmail: ${party.emailId}\nStatus: ${party.isActive ? 'Active' : 'Inactive'}`);
  };

  const handleEdit = (party: Party) => {
    setEditingParty(party);
    setIsModalOpen(true);
  };

  const handleDelete = async (party: Party) => {
    if (window.confirm(`Are you sure you want to delete ${party.partyName}?`)) {
      try {
        const response = await deletePartyAPI(party.id || 0);
        if (response.isSuccess) {
          await loadParties();
          showSuccess('Party deleted successfully!');
        } else {
          showError(response.message || 'Failed to delete party');
        }
      } catch (error) {
        console.error('Failed to delete party:', error);
        showError('Failed to delete party. Please try again.');
      }
    }
  };

  // Filter parties based on search and status
  const filteredParties = parties.filter((party) => {
    const matchesSearch =
      party.partyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      party.mobileNo.toLowerCase().includes(searchQuery.toLowerCase()) ||
      party.emailId.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus =
      statusFilter === 'all' || 
      (statusFilter === 'active' && party.isActive) ||
      (statusFilter === 'inactive' && !party.isActive);
    
    return matchesSearch && matchesStatus;
  });

  return (
    <div className={styles.container}>
      {/* Toast Container */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.title}>Party Master</h1>
          <p className={styles.subtitle}>Manage your parties and their accounts</p>
        </div>
        {permissions.canAdd && (
          <button className={styles.addButton} onClick={handleAddParty}>+ Add Party</button>
        )}
      </div>

      {/* Search and Filter Bar */}
      <div className={styles.searchFilterBar}>
        <input
          type="text"
          placeholder="Search by name, mobile, or email..."
          className={styles.searchInput}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <select
          className={styles.statusFilter}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Table */}
      <div className={styles.tableContainer}>
        {loading ? (
          <div className={styles.loading}>Loading parties...</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>NAME</th>
                <th>PHONE</th>
                <th>EMAIL</th>
                <th>PAN</th>
                <th>STATUS</th>
                {(permissions.canEdit || permissions.canVoid) && <th>ACTIONS</th>}
              </tr>
            </thead>
            <tbody>
              {filteredParties.length === 0 ? (
                <tr>
                  <td colSpan={permissions.canEdit || permissions.canVoid ? 6 : 5} className={styles.emptyState}>No parties found</td>
                </tr>
              ) : (
                filteredParties.map((party) => (
                  <tr key={party.id}>
                    <td>{party.partyName}</td>
                    <td>{party.mobileNo}</td>
                    <td>{party.emailId}</td>
                    <td>{party.panNo}</td>
                    <td>
                      <span className={`${styles.statusBadge} ${styles[party.isActive ? 'active' : 'inactive']}`}>
                        {party.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {(permissions.canEdit || permissions.canVoid) && (
                      <td>
                        <div className={styles.actions}>
                          {permissions.canEdit && (
                            <button
                              className={styles.actionIcon}
                              onClick={() => handleEdit(party)}
                              title="Edit"
                            >
                              ✏️
                            </button>
                          )}
                          {permissions.canVoid && (
                            <button
                              className={`${styles.actionIcon} ${styles.deleteIcon}`}
                              onClick={() => handleDelete(party)}
                              title="Delete"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Party Modal */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={handleCloseModal} 
        title={editingParty ? 'Edit Party' : 'Add New Party'}
      >
        <AddPartyForm 
          onSubmit={handleSubmitParty} 
          onCancel={handleCloseModal}
          initialData={editingParty ? {
            name: editingParty.partyName,
            mobile: editingParty.mobileNo,
            email: editingParty.emailId,
            address: editingParty.address,
            pan: editingParty.panNo,
            status: editingParty.isActive ? 'active' : 'inactive',
            note: editingParty.notes,
          } : undefined}
        />
      </Modal>
    </div>
  );
};

export default PartyMaster;
